import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEventStore } from '../../../hooks/useEventStore';
import { useEventGenreColors, findGenreColor } from '../../../hooks/useGenreColors';
import { artistsData } from '../../../mocks/artists';
import type { PrefectureEvent } from '../../../mocks/events';
import { supabase } from '../../../lib/supabase';

const categoryIconMap: Record<string, string> = {
  'ALL': 'ri-calendar-event-line',
  'LIVE': 'ri-music-2-line',
  'DJ EVENT': 'ri-disc-line',
  'DJ': 'ri-disc-line',
  'WORKSHOP': 'ri-tools-line',
  'MARKET': 'ri-store-2-line',
  'EXHIBITION': 'ri-gallery-line',
  'ライブ': 'ri-music-2-line',
  'ワークショップ': 'ri-tools-line',
  'フェス': 'ri-flag-line',
  'トークイベント': 'ri-chat-3-line',
  '展示': 'ri-gallery-line',
};

const DEFAULT_COLOR = '#00d9ff';

interface EventAttribute {
  id: string;
  name: string;
  color: string;
}

interface EventMainGenre {
  id: string;
  name: string;
}

interface EventMainPart {
  id: string;
  name: string;
}

interface EnrichedEvent extends PrefectureEvent {
  attributeName?: string;
  mainGenreName?: string;
  mainPartName?: string;
  displayColor?: string;
}

function formatDate(dateStr: string): { month: string; day: string; weekday: string } {
  const date = new Date(dateStr);
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return {
    month: months[date.getMonth()],
    day: String(date.getDate()),
    weekday: weekdays[date.getDay()],
  };
}

function calcCountdown(dateStr: string): { days: number; status: 'future' | 'today' | 'past' } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDate = new Date(dateStr);
  const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
  const diffMs = eventDay.getTime() - today.getTime();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return { days: 0, status: 'today' };
  if (days > 0) return { days, status: 'future' };
  return { days: Math.abs(days), status: 'past' };
}

function getArtistDisplayNames(event: PrefectureEvent): string[] {
  const names: string[] = [];
  for (const id of event.artistIds) {
    const found = artistsData.find((a) => a.id === id);
    if (found) names.push(found.name);
  }
  for (const name of event.artistNames) {
    names.push(name);
  }
  return names;
}

function EventCard({ event, genreColorMap }: { event: EnrichedEvent; genreColorMap: Record<string, string> }) {
  const navigate = useNavigate();
  const { month, day, weekday } = formatDate(event.date);
  const countdown = calcCountdown(event.date);
  
  // Use attribute name for icon, fallback to category
  const iconKey = event.attributeName || event.category;
  const icon = categoryIconMap[iconKey] || 'ri-calendar-line';
  
  // Use display color (attribute color) if available, otherwise fallback
  const genreColor = event.displayColor || findGenreColor(event.category, genreColorMap, event.color);

  // カウントダウンバッジの設定
  const countdownBadge = (() => {
    if (countdown.status === 'today') {
      return {
        label: '本日開催！',
        icon: 'ri-live-line',
        bgColor: '#00ff41',
        textColor: '#00ff41',
        borderColor: '#00ff4160',
        pulse: true,
      };
    }
    if (countdown.status === 'future') {
      if (countdown.days <= 3) {
        return {
          label: `あと ${countdown.days} 日`,
          icon: 'ri-alarm-line',
          bgColor: '#ff4444',
          textColor: '#ff4444',
          borderColor: '#ff444460',
          pulse: true,
        };
      }
      if (countdown.days <= 7) {
        return {
          label: `あと ${countdown.days} 日`,
          icon: 'ri-time-line',
          bgColor: '#ff9500',
          textColor: '#ff9500',
          borderColor: '#ff950060',
          pulse: false,
        };
      }
      if (countdown.days <= 30) {
        return {
          label: `あと ${countdown.days} 日`,
          icon: 'ri-calendar-event-line',
          bgColor: genreColor,
          textColor: genreColor,
          borderColor: `${genreColor}60`,
          pulse: false,
        };
      }
      return null;
    }
    return null;
  })();

  return (
    <div
      className="flex-shrink-0 w-full sm:w-[300px] md:w-[320px] rounded-xl border overflow-hidden transition-all duration-300 cursor-pointer group hover:-translate-y-1"
      style={{
        borderColor: `${genreColor}20`,
        background: '#0d0d0d',
      }}
      onClick={() => navigate(`/event/${event.id}`)}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${genreColor}20`;
        (e.currentTarget as HTMLElement).style.borderColor = `${genreColor}50`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
        (e.currentTarget as HTMLElement).style.borderColor = `${genreColor}20`;
      }}
    >
      {/* Thumbnail */}
      <div className="relative w-full h-[160px] overflow-hidden">
        <img
          src={event.thumbnail}
          alt={event.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-black/30 to-transparent" />

        {/* Badges row */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          {/* Featured badge */}
          {event.isFeatured && (
            <div className="px-2 py-0.5 rounded-full bg-neon-green/20 border border-neon-green/40 backdrop-blur-sm">
              <span className="text-neon-green text-[8px] font-bold tracking-wider">FEATURED</span>
            </div>
          )}
          {/* NODE EVENT badge */}
          {event.created_by && (
            <div
              className="px-2 py-0.5 rounded-full backdrop-blur-sm flex items-center gap-1"
              style={{ background: 'rgba(0,217,255,0.15)', border: '1px solid rgba(0,217,255,0.4)' }}
            >
              <span className="w-1 h-1 rounded-full bg-[#00d9ff]" style={{ boxShadow: '0 0 4px #00d9ff' }} />
              <span className="text-[#00d9ff] text-[8px] font-bold tracking-wider">NODE EVENT</span>
            </div>
          )}
        </div>

        {/* Category badge - Hierarchy display */}
        <div className="absolute top-3 right-3">
          {event.attributeName ? (
            <div className="flex flex-col gap-1">
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-sm"
                style={{
                  background: `${genreColor}30`,
                  color: genreColor,
                  border: `1px solid ${genreColor}40`,
                }}
              >
                <i className={`${icon} mr-1`} />
                {event.attributeName}
              </span>
              {(event.mainGenreName || event.mainPartName) && (
                <div className="flex flex-col gap-0.5 px-2">
                  {event.mainGenreName && (
                    <span className="text-gray-400 text-[9px]">→ {event.mainGenreName}</span>
                  )}
                  {event.mainPartName && (
                    <span className="text-gray-400 text-[9px]">→ {event.mainPartName}</span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider backdrop-blur-sm"
              style={{
                background: `${genreColor}30`,
                color: genreColor,
                border: `1px solid ${genreColor}40`,
              }}
            >
              <i className={`${icon} mr-1`} />
              {event.category}
            </span>
          )}
        </div>

        {/* Date badge on image */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <div
            className="w-12 h-14 rounded-lg flex flex-col items-center justify-center backdrop-blur-md"
            style={{
              background: `${genreColor}15`,
              border: `1px solid ${genreColor}30`,
            }}
          >
            <span className="text-[9px] tracking-wider" style={{ color: genreColor }}>{month}</span>
            <span className="text-white text-lg font-bold leading-none">{day}</span>
            <span className="text-gray-400 text-[8px] tracking-wider">{weekday}</span>
          </div>

          {/* カウントダウンバッジ */}
          {countdownBadge && (
            <div
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold tracking-wider backdrop-blur-md ${countdownBadge.pulse ? 'animate-pulse' : ''}`}
              style={{
                background: `${countdownBadge.bgColor}18`,
                color: countdownBadge.textColor,
                border: `1px solid ${countdownBadge.borderColor}`,
                boxShadow: `0 0 10px ${countdownBadge.bgColor}25`,
              }}
            >
              <i className={`${countdownBadge.icon} text-[9px]`} />
              {countdownBadge.label}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        <h4 className="text-white text-sm font-bold tracking-wide mb-2 truncate group-hover:text-white/90">
          {event.title}
        </h4>

        {/* Description */}
        <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2 min-h-[2.5rem]">
          {event.description}
        </p>

        {/* Artists */}
        {getArtistDisplayNames(event).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {getArtistDisplayNames(event).map((name) => (
              <span
                key={name}
                className="px-2 py-0.5 rounded-full text-[10px] tracking-wider bg-white/5 text-gray-400 border border-white/10"
              >
                {name}
              </span>
            ))}
          </div>
        )}

        {/* Venue & Time */}
        <div className="space-y-1.5 pt-3 border-t border-white/5">
          <div className="flex items-center gap-1.5">
            <i className="ri-map-pin-line text-gray-600 text-xs flex-shrink-0 w-3 h-3 flex items-center justify-center" />
            <span className="text-gray-500 text-[11px] truncate">{event.venue}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <i className="ri-time-line text-gray-600 text-xs flex-shrink-0 w-3 h-3 flex items-center justify-center" />
            <span className="text-gray-500 text-[11px] truncate">{event.time}</span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
              <i className="ri-ticket-line text-xs flex-shrink-0 w-3 h-3 flex items-center justify-center" style={{ color: genreColor }} />
              <span className="text-xs font-bold tracking-wider" style={{ color: genreColor }}>
                {event.price}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EventList() {
  const [activeCategory, setActiveCategory] = useState('ALL');
  const scrollRef = useRef<HTMLDivElement>(null);
  const { events, loading } = useEventStore();
  const genreColorMap = useEventGenreColors();
  const [enrichedEvents, setEnrichedEvents] = useState<EnrichedEvent[]>([]);
  const [attributes, setAttributes] = useState<EventAttribute[]>([]);

  // Fetch event attributes, genres, parts
  useEffect(() => {
    const fetchHierarchyData = async () => {
      try {
        const attributeIds = [...new Set(events.map(e => e.attributeId).filter(Boolean))];
        const genreIds = [...new Set(events.map(e => e.mainGenreId).filter(Boolean))];
        const partIds = [...new Set(events.map(e => e.mainPartId).filter(Boolean))];

        const [attributesRes, genresRes, partsRes] = await Promise.all([
          attributeIds.length > 0 ? supabase.from('event_attributes').select('id, name, color').in('id', attributeIds) : { data: [] },
          genreIds.length > 0 ? supabase.from('event_main_genres').select('id, name').in('id', genreIds) : { data: [] },
          partIds.length > 0 ? supabase.from('event_main_parts').select('id, name').in('id', partIds) : { data: [] },
        ]);

        const attributeMap = new Map((attributesRes.data || []).map(a => [a.id, a]));
        const genreMap = new Map((genresRes.data || []).map(g => [g.id, g]));
        const partMap = new Map((partsRes.data || []).map(p => [p.id, p]));

        // Get unique attributes for filter buttons
        const uniqueAttributes = Array.from(attributeMap.values());
        setAttributes(uniqueAttributes);

        // Enrich events with hierarchy data
        const enriched: EnrichedEvent[] = events.map(event => {
          const attr = event.attributeId ? attributeMap.get(event.attributeId) : null;
          const genre = event.mainGenreId ? genreMap.get(event.mainGenreId) : null;
          const part = event.mainPartId ? partMap.get(event.mainPartId) : null;

          return {
            ...event,
            attributeName: attr?.name,
            mainGenreName: genre?.name,
            mainPartName: part?.name,
            displayColor: attr?.color || event.color,
          };
        });

        setEnrichedEvents(enriched);
      } catch (error) {
        console.error('Error fetching hierarchy data:', error);
        setEnrichedEvents(events);
      }
    };

    fetchHierarchyData();
  }, [events]);

  // Build categories from attributes
  const categories = useMemo(() => {
    const cats: Array<{ id: string; name: string; color: string }> = [
      { id: 'ALL', name: 'すべて', color: DEFAULT_COLOR }
    ];
    
    attributes.forEach(attr => {
      cats.push({ id: attr.id, name: attr.name, color: attr.color });
    });

    return cats;
  }, [attributes]);

  const filteredEvents = activeCategory === 'ALL'
    ? enrichedEvents
    : enrichedEvents.filter((e) => e.attributeId === activeCategory);

  const handleCategoryClick = (cat: string) => {
    setActiveCategory(cat);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-4 lg:mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1.5 h-1.5 rounded-full bg-neon-blue" style={{ boxShadow: '0 0 8px #00d9ff' }} />
              <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Upcoming Events</span>
            </div>
            <h3 className="text-white font-display text-xl lg:text-2xl tracking-wider">イベント情報</h3>
          </div>
        </div>
        <div className="flex items-center justify-center py-16">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 border-2 border-neon-blue/20 rounded-full" />
            <div className="absolute inset-0 border-2 border-neon-blue border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 lg:mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1.5 h-1.5 rounded-full bg-neon-blue" style={{ boxShadow: '0 0 8px #00d9ff' }} />
            <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Upcoming Events</span>
          </div>
          <h3 className="text-white font-display text-xl lg:text-2xl tracking-wider">イベント情報</h3>
        </div>
        <span className="text-neon-blue text-sm font-bold">{filteredEvents.length} 件</span>
      </div>

      {/* Category Buttons */}
      <div className="relative mb-5">
        <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
        <div
          className="flex gap-2 overflow-x-auto pb-2 px-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat) => {
            const isActive = activeCategory === cat.id;
            const icon = categoryIconMap[cat.name] || categoryIconMap['ALL'];

            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full border whitespace-nowrap text-xs font-bold tracking-wider transition-all duration-300 cursor-pointer flex-shrink-0 ${
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'bg-transparent text-gray-500 border-white/10 hover:bg-white/5 hover:text-gray-300'
                }`}
                style={{
                  borderColor: isActive ? `${cat.color}60` : undefined,
                  boxShadow: isActive ? `0 0 16px ${cat.color}20` : undefined,
                }}
              >
                <i
                  className={`${icon} text-sm w-4 h-4 flex items-center justify-center`}
                  style={{ color: isActive ? cat.color : undefined }}
                />
                <span>{cat.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Horizontal Scroll Cards */}
      <div className="relative">
        <div className="hidden sm:block absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
        <div className="hidden sm:block absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />

        {/* Mobile: 1列縦並び */}
        <div className="sm:hidden flex flex-col gap-4">
          {filteredEvents.map((event) => (
            <div key={event.id} className="w-full">
              <EventCard event={event} genreColorMap={genreColorMap} />
            </div>
          ))}
          {filteredEvents.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <i className="ri-calendar-line text-3xl text-gray-700 mb-3" />
              <p className="text-gray-600 text-sm">このカテゴリのイベントはまだありません</p>
            </div>
          )}
        </div>

        {/* PC: 横スクロール */}
        <div
          ref={scrollRef}
          className="hidden sm:flex gap-4 overflow-x-auto pb-4 px-1 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {filteredEvents.map((event) => (
            <div key={event.id} className="snap-start">
              <EventCard event={event} genreColorMap={genreColorMap} />
            </div>
          ))}

          {filteredEvents.length === 0 && (
            <div className="flex-shrink-0 w-full flex flex-col items-center justify-center py-12 text-center">
              <i className="ri-calendar-line text-3xl text-gray-700 mb-3" />
              <p className="text-gray-600 text-sm">このカテゴリのイベントはまだありません</p>
            </div>
          )}
        </div>
      </div>

      {/* Scroll hint */}
      {filteredEvents.length > 1 && (
        <div className="hidden sm:flex items-center justify-center gap-2 mt-2">
          <i className="ri-arrow-left-right-line text-gray-700 text-xs" />
          <span className="text-gray-700 text-[10px] tracking-wider">SWIPE</span>
        </div>
      )}

      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
